import { render } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
}

let active = false;

/** The native modal supplies top-layer stacking, background inertness and keyboard focus containment. */
function Confirmation(props: ConfirmationOptions & { answer: (confirmed: boolean) => void }) {
  const modal = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const confirm = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const el = modal.current!;
    el.showModal();
    cancel.current!.focus();
    return () => el.close();
  }, []);
  return (
    <dialog
      ref={modal}
      class="confirmation"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-message"
      onCancel={(e) => { e.preventDefault(); props.answer(false); }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Tab") {
          e.preventDefault();
          (document.activeElement === cancel.current ? confirm.current : cancel.current)!.focus();
        }
      }}
      onClose={() => props.answer(false)}
    >
      <h2 id="confirmation-title">{props.title}</h2>
      <p id="confirmation-message">{props.message}</p>
      <div class="confirmation-actions">
        <button type="button" ref={cancel} onClick={() => props.answer(false)}>Cancel</button>
        <button type="button" ref={confirm} class={props.destructive ? "danger" : "primary"} onClick={() => props.answer(true)}>{props.confirmLabel}</button>
      </div>
    </dialog>
  );
}

/** Await a decision without replacing the underlying page, sheet or its inline errors. */
export function confirmAction(options: ConfirmationOptions): Promise<boolean> {
  // A double activation must not stack dialogs or run a destructive action twice.
  if (active) return Promise.resolve(false);
  active = true;
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const fallback = opener?.closest<HTMLElement>(".sheet, main");
  const container = document.body.appendChild(document.createElement("div"));
  const { promise, resolve } = Promise.withResolvers<boolean>();
  let answered = false;
  const answer = (confirmed: boolean) => {
    if (answered) return;
    answered = true;
    render(null, container);
    container.remove();
    active = false;
    if (opener?.isConnected) opener.focus();
    else if (fallback?.isConnected) {
      const tabIndex = fallback.getAttribute("tabindex");
      fallback.tabIndex = -1;
      fallback.focus();
      if (tabIndex === null) fallback.removeAttribute("tabindex");
      else fallback.setAttribute("tabindex", tabIndex);
    }
    resolve(confirmed);
  };
  render(<Confirmation {...options} answer={answer} />, container);
  return promise;
}
